import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { apiClient } from '@/shared/lib/api'
import {
  clearStoredAuthSession,
  hasRefreshToken,
  isAuthSessionFresh,
  refreshAuthSession,
  setStoredAuthSession,
  subscribeToAuthSessionCleared,
} from '@/shared/lib/auth-session'
import { clearPersistedQueryCache, clearUserScopedQueries } from '@/shared/lib/query-client'
import { useCountryStore } from '@/shared/stores/country'

const USER_ID_KEY = 'auth_user_id'

export interface User {
  id: number
  email: string | null
  emailConfirmed?: boolean
  pendingEmailChange?: boolean
  emailSettings?: EmailSettings | null
  name?: string | null
  username?: string | null
  balance?: number
  pendingBalance?: number
  usdBalance?: number
  pendingUsdBalance?: number
  lifetimeShopping?: number
  lifetimeReferrals?: number
  numReferrals?: number
  withdrawalsEnabled?: boolean
  autoWithdraw?: boolean
  referralCode?: string
  referralUrl?: string
  lightningAddress?: string | null
  lnurlKey?: string | null
  auth47Key?: string | null
  notificationSettings?: NotificationSettings | null
  prefCountry?: string | null
  preferredLanguage?: string | null
}

export interface EmailSettings {
  marketing: boolean
  productUpdates: boolean
  receipts: boolean
}

export interface NotificationSettings {
  newMerchants: boolean
  promotions: boolean
  referralBonuses: boolean
  referralSignUps: boolean
  weeklyRewardsSummary: boolean
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  refreshSession: () => Promise<boolean>
  checkSession: () => Promise<void>
  logout: () => Promise<void>
}

function clearClientAuthState() {
  clearStoredAuthSession()
  localStorage.removeItem(USER_ID_KEY)
}

function syncAuthenticatedUser(user: User) {
  const userId = String(user.id)
  const previousUserId = localStorage.getItem(USER_ID_KEY)
  if (previousUserId && previousUserId !== userId) {
    clearUserScopedQueries()
  }

  localStorage.setItem(USER_ID_KEY, userId)
  if (user.prefCountry) {
    localStorage.setItem('preferredCountry', user.prefCountry)
    useCountryStore.getState().setCountry(user.prefCountry)
  }
}

const storedSessionIsFresh = hasRefreshToken() && isAuthSessionFresh()

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      isAuthenticated: storedSessionIsFresh,
      isLoading: true,
      setUser: (user) => {
        if (user) {
          syncAuthenticatedUser(user)
        }
        set({ user, isAuthenticated: !!user, isLoading: false })
      },
      setToken: (token) => {
        setStoredAuthSession(token)
        set({ isAuthenticated: !!token, isLoading: false })
      },
      refreshSession: async () => {
        if (!hasRefreshToken()) {
          clearClientAuthState()
          set({ user: null, isAuthenticated: false, isLoading: false })
          return false
        }

        try {
          await refreshAuthSession()
          set({ isAuthenticated: true, isLoading: false })
          return true
        } catch {
          clearClientAuthState()
          set({ user: null, isAuthenticated: false, isLoading: false })
          return false
        }
      },
      checkSession: async () => {
        set({ isAuthenticated: isAuthSessionFresh() && hasRefreshToken() })

        if (!isAuthSessionFresh() && hasRefreshToken()) {
          try {
            await refreshAuthSession()
            set({ isAuthenticated: true })
          } catch {
            clearClientAuthState()
            set({ user: null, isAuthenticated: false, isLoading: false })
            return
          }
        }

        if (!hasRefreshToken()) {
          clearClientAuthState()
          set({ user: null, isAuthenticated: false, isLoading: false })
          return
        }

        try {
          const user = await apiClient<User>('/users/me')
          syncAuthenticatedUser(user)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch {
          clearClientAuthState()
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },
      logout: async () => {
        try {
          await apiClient('/auth/logout', { method: 'POST', skipAuthRefresh: true })
        } catch {
          // Local auth state is cleared even if the API logout request fails.
        } finally {
          clearClientAuthState()
          clearUserScopedQueries()
          try {
            await clearPersistedQueryCache()
          } catch {
            // Purging the persisted cache is best-effort; logout must never throw.
          }
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },
    }),
    { name: 'auth-store' }
  )
)

subscribeToAuthSessionCleared(() => {
  localStorage.removeItem(USER_ID_KEY)
  clearUserScopedQueries()
  // Session expiry (not just explicit logout) must also drop persisted
  // user data so a different account never hydrates it. Best-effort.
  void clearPersistedQueryCache().catch(() => {})
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false })
})
